import axios from "axios";
import { refreshSession } from "./authClient";

const API_BASE = process.env.REACT_APP_API_URL || "";

const subscriptionClient = axios.create({
    baseURL: API_BASE,
    withCredentials: true
});

// Add response interceptor for 401 handling
subscriptionClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                await refreshSession();
                return subscriptionClient(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Create a checkout session for subscription.
 * @param {object} options
 * @param {boolean} [options.withTrial=false] - Start with a 14-day free trial
 * @param {"monthly"|"annual"} [options.plan="monthly"] - Billing cadence
 * @returns {Promise<{checkoutUrl: string}>} The Stripe checkout URL
 */
export async function createCheckoutSession({ withTrial = false, plan = "monthly" } = {}) {
    const params = new URLSearchParams({ trial: String(withTrial), plan });
    const resp = await subscriptionClient.post(`/api/subscription/checkout?${params}`);
    return resp.data;
}

/**
 * Get the current subscription status.
 * @returns {Promise<{status: string, currentPeriodEnd: string, trialEndsAt: string, isActive: boolean}>}
 */
export async function getSubscriptionStatus() {
    const resp = await subscriptionClient.get("/api/subscription/status");
    return resp.data;
}

/**
 * Verify a checkout session and activate the subscription directly.
 * Used when the webhook hasn't arrived yet.
 * @param {string} sessionId - The Stripe checkout session ID
 * @returns {Promise<{status: string, isActive: boolean}>}
 */
export async function verifyCheckoutSession(sessionId) {
    const resp = await subscriptionClient.post("/api/subscription/verify-session", { sessionId });
    return resp.data;
}

/**
 * Create a customer portal session for managing subscription.
 * @returns {Promise<{portalUrl: string}>} The Stripe customer portal URL
 */
export async function createPortalSession() {
    const resp = await subscriptionClient.post("/api/subscription/portal");
    return resp.data;
}
